import { useReducer } from "react";
import { AppContext } from "app_context";
import { Redirect, Route, Router, Switch } from "react-router-dom";
import ErrorLayout from "layouts/ErrorLayout";
import AuthLayout from "layouts/Auth";
import MainLayout from "layouts/MainLayout";
import SweetAlert from "react-bootstrap-sweetalert";
import { createBrowserHistory } from "history";
import {
  ADD_ALERT,
  REMOVE_ALERT,
  SHOW_PRIVACY_SETTINGS
} from "constants/action_types";
import { CognitoAuthTrackingProvider } from "components/Authentication/CognitoAuthTrackingContext";

import Preloader from "components/Preloader/Preloader";
import CookieAndPrivacy from "components/CookieAndPrivacy";
import { useMatomo } from "@jonkoops/matomo-tracker-react";
import { areCookiesSelected } from "core/cookie";
import {
  ThemeProvider as MuiThemeProvider,
  StyledEngineProvider
} from "@mui/material/styles";
import StylesProvider from "@mui/styles/StylesProvider";
import theme from "theme";
import { PermissionsProvider } from "contexts/PermissionsProvider";
import { AxiosProvider } from "contexts/AxiosContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { LocaleProvider } from "contexts/LocaleContext";

const hist = createBrowserHistory();

function reducer(state, action) {
  switch (action.type) {
    case ADD_ALERT:
      return { ...state, modals: [...state.modals, action.modal] };
    case REMOVE_ALERT:
      return {
        ...state,
        modals: [...state.modals].filter(m => m.id !== action.id)
      };
    case SHOW_PRIVACY_SETTINGS:
      return { ...state, showPrivacySettings: action.isShown };
    default:
      return state;
  }
}

function addModal(modal) {
  return { type: ADD_ALERT, modal };
}

function removeModal(id) {
  return { type: REMOVE_ALERT, id };
}

function showPrivacySettings(isShown) {
  return { type: SHOW_PRIVACY_SETTINGS, isShown };
}

// eslint-disable-next-line react/prop-types
function GuardedRoute({ component: Component, ...rest }) {
  return (
    <Route {...rest}>
      <AxiosProvider>
        <PermissionsProvider>
          <Component />
        </PermissionsProvider>
      </AxiosProvider>
    </Route>
  );
}

function isBrowserCompatible() {
  let result = true;

  if (window.Modernizr) {
    const m = window.Modernizr;
    const checks = [
      "svg",
      "json",
      "queryselector",
      "filereader",
      "fetch",
      "urlsearchparams",
      "websocketsbinary",
      "atobbtoa"
    ];
    for (const elem of checks) {
      if (!m[elem]) {
        result = false;
        break;
      }
    }
  }

  return result;
}

function InCompatibleBrowserMessage() {
  return (
    <Preloader
      text={"There is a problem."}
      showLogout={false}
      failure={true}
      failureText={
        "The application doesn't support your browser. Supported browsers: Firefox/Gecko 81+, Chrome/Webkit/Edge 80+ and latest Safari."
      }
    />
  );
}

const queryClient = new QueryClient();

function App() {
  /*
   * Matomo provides the option to track outbound link, however, this implementation is flaky for a
   * SPA (Single Page Application) without SSR (Server Side Rendering) across different versions of Matomo.
   * Therefore you can use the enableLinkTracking method to listen to outbound clicks on anchor elements.
   * This method should be placed on a component directly below your MatomoProvider on a component that's
   * rendered on every page view. Also, make sure to disable the linkTracking option on the instance passed
   * to the provider to prevent Matomo from catching some link clicks:
   */
  const { enableLinkTracking } = useMatomo();
  enableLinkTracking();

  const [globalState, dispatcher] = useReducer(reducer, {
    modals: [],
    showPrivacySettings: !areCookiesSelected()
  });

  const closeModal = id => dispatch => {
    dispatch(removeModal(id));
  };

  const showModal = modalProps => {
    if (!modalProps.id) {
      modalProps.id = "" + Date.now() + Math.random() + Math.random();
    }

    // auto-close on cancel
    if (modalProps.onConfirm) {
      const onConfirm = modalProps.onConfirm;
      modalProps.onConfirm = (...args) => {
        onConfirm(...args);
        closeModal(modalProps.id)(dispatcher);
      };
    } else {
      modalProps.onConfirm = () => closeModal(modalProps.id)(dispatcher);
    }

    // auto-close on cancel
    if (modalProps.onCancel) {
      const onCancel = modalProps.onCancel;
      modalProps.onCancel = (...args) => {
        onCancel(...args);
        closeModal(modalProps.id)(dispatcher);
      };
    } else {
      modalProps.onCancel = () => closeModal(modalProps.id)(dispatcher);
    }

    dispatcher(addModal(modalProps));
  };

  const showPrivacySetting = privacySettingsProps => {
    dispatcher(showPrivacySettings(privacySettingsProps));
  };

  const context = {
    showModal,
    showPrivacySetting
  };

  const visibleModal =
    globalState.modals && globalState.modals.length > 0
      ? globalState.modals[0]
      : null;

  const visiblesPrivacySettings = globalState.showPrivacySettings;

  if (!isBrowserCompatible()) {
    return <InCompatibleBrowserMessage />;
  }

  return (
    <AppContext.Provider value={context}>
      <StylesProvider injectFirst>
        <StyledEngineProvider injectFirst>
          <MuiThemeProvider theme={theme}>
            <LocaleProvider>
              <QueryClientProvider client={queryClient}>
                <Router history={hist} basename="/">
                  <CognitoAuthTrackingProvider>
                    <Switch>
                      <Route
                        exact
                        path="/no-access"
                        component={ErrorLayout}
                        notFoundError={false}
                      />
                      <Route
                        exact
                        path="/error"
                        component={ErrorLayout}
                        notFoundError={false}
                      />
                      <Route
                        exact
                        path="/cke403"
                        component={ErrorLayout}
                        notFoundError={false}
                      />
                      <Route
                        exact
                        path="/maintenance"
                        component={ErrorLayout}
                        notFoundError={false}
                      />
                      <Route path={"/login"} component={AuthLayout} />
                      <GuardedRoute path="/pages" component={MainLayout} />
                      <Route exact path="/">
                        <Redirect to="/pages" />
                      </Route>
                      <Route
                        path="*"
                        component={ErrorLayout}
                        notFoundError={true}
                      />
                    </Switch>
                    {visibleModal && (
                      <SweetAlert {...visibleModal}>
                        {visibleModal.content}
                      </SweetAlert>
                    )}
                    <CookieAndPrivacy
                      showPrivacySettings={visiblesPrivacySettings}
                    />
                  </CognitoAuthTrackingProvider>
                </Router>
                <ReactQueryDevtools initialIsOpen={false} />
              </QueryClientProvider>
            </LocaleProvider>
          </MuiThemeProvider>
        </StyledEngineProvider>
      </StylesProvider>
    </AppContext.Provider>
  );
}

export { App };
